/**
 * Class constants for the account panel.
 *
 * DESIGN INTENT — THIS IS NOT A SECOND SIDEBAR
 *
 * A right-edge panel in an admin console defaults to "drawer", and a drawer
 * reads as a lesser copy of the navigation already on the left. This one is
 * deliberately different in kind, not just in position:
 *
 *   - It is a COLUMN, not a rail. The sidebar answers "where can I go" for the
 *     whole product. This answers "who am I, and what do I do next" for one
 *     person, so it gets a wider measure, larger type, and room for a sentence
 *     under each destination.
 *   - Its identity block is inverted. A tinted accent field at the top of the
 *     panel — the same surface treatment as an active nav row — makes the panel
 *     announce itself as a different surface the moment it opens.
 *   - Every row is a two-line card with a description, not a 36px nav row. The
 *     words are the point; the icon is wayfinding.
 *
 * The one memorable element is the identity block's accent field. Everything
 * below it is quiet and disciplined on purpose.
 */

/** Panel width. Wider than the mobile drawer's `w-72` because rows carry two lines. */
export const accountPanelSheetClasses =
  'w-[21rem] max-w-[92vw] border-l border-border/80 bg-surface sm:w-[23.5rem]';

/**
 * The identity field.
 *
 * Tinted with `accent-soft` rather than a flat surface, and this is the panel's
 * signature: the accent field is the visual cue that separates "you" from
 * "the product", and it lands before any text is read. It also reuses a token
 * that already means "this is the current thing" in the sidebar, so the panel
 * inherits the product's existing meaning rather than inventing a second one.
 */
export const accountPanelIdentityClasses = 'relative shrink-0 bg-accent-soft px-5 pb-5 pt-4';

export const accountPanelIdentityTopRowClasses = 'flex items-start justify-between gap-3';

export const accountPanelAvatarClasses =
  'flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent text-xl font-semibold tracking-wide text-accent-content select-none';

export const accountPanelNameClasses =
  'mt-3 text-2xl font-semibold leading-tight text-content break-words';

export const accountPanelRoleClasses = 'mt-1 text-md leading-snug text-content-muted';

/** Workspace chip — sits on the accent field, so it carries a border and a solid fill. */
export const accountPanelWorkspaceClasses =
  'mt-3 inline-flex max-w-full items-center gap-1.5 rounded-full border border-border bg-surface px-2.5 py-1 text-xs font-medium text-content';

export const accountPanelCloseClasses =
  'flex h-8 w-8 shrink-0 items-center justify-center rounded-control border border-border bg-surface text-content-muted transition-colors hover:border-border-strong hover:text-content';

/**
 * "Jump to" — a grid of tiles, not a list of rows.
 *
 * WHY A GRID
 *
 * The list version was a worse sidebar: same shape, same left-aligned icon,
 * same one-line label, just narrower and further right. A grid changes the
 * reading mode, and that is the point — a list invites sequential scanning, so
 * four items means reading four lines to find one. A grid invites recognition:
 * you learn "the blue one is Tenants" and then you stop reading entirely, which
 * is exactly the behaviour a shortcut should reward.
 *
 * Colour carries NO meaning here. The tints tell the tiles apart; they do not
 * say a tile is important, urgent or broken. Nothing in this panel is
 * understood only by its colour — every tile is named in full underneath.
 */
export const accountPanelGridClasses = 'grid grid-cols-3 gap-2 px-4 pb-1 pt-1';

/**
 * A tile carries NO border.
 *
 * The chip inside is already a filled, coloured shape; wrapping it in a border
 * gives the tile two competing edges and the grid reads as a table of boxes.
 * Without the border the chip is the shape, and the only edge in the tile is
 * the one the colour draws. Hover fills the tile instead of outlining it, so
 * the whole cell is the target rather than a rectangle inside it.
 */
export const accountPanelTileClasses =
  'group flex flex-col items-center gap-2 rounded-surface px-1 py-2.5 text-center transition-colors hover:bg-surface-sunken';

/** The tinted icon chip. Colour is applied per tile, so this is the geometry only. */
export const accountPanelTileIconWrapClasses =
  'flex h-11 w-11 items-center justify-center rounded-surface transition-transform group-hover:scale-105';

export const accountPanelTileIconClasses = 'h-5 w-5';

export const accountPanelTileLabelClasses =
  'text-2xs font-semibold leading-tight text-content line-clamp-2';

/**
 * Tint cycles, in tile order.
 *
 * Applied by INDEX rather than by destination, so a tile keeps a stable colour
 * as long as its position is stable, and adding a fourth destination does not
 * reshuffle the three the user has already learned. Keying colour to the
 * destination id instead would give each one a fixed identity, which sounds
 * better until the list changes and every colour moves.
 *
 * A tint that reappears is fine. These are wayfinding cues, not identifiers.
 */
export const accountPanelTileTints = [
  { wrap: 'bg-tint-one-soft text-tint-one', label: 'One' },
  { wrap: 'bg-tint-two-soft text-tint-two', label: 'Two' },
  { wrap: 'bg-tint-three-soft text-tint-three', label: 'Three' },
] as const;

/**
 * Menu.
 *
 * Rows are 12px-padded two-line cards with a hairline between them. The
 * hairline is doing real work: without it the descriptions of adjacent rows
 * read as one paragraph, because the rows are text-heavy by design.
 */
export const accountPanelMenuClasses = 'flex-1 overflow-y-auto pb-2';

export const accountPanelSectionLabelClasses =
  'px-4 pb-1 pt-3 text-xs font-bold uppercase tracking-wider text-content-muted/70 select-none';

/**
 * Rows are full-bleed, with the hairline spanning the whole panel width.
 *
 * The hairlines do real work: without them the descriptions of adjacent rows
 * read as one paragraph, because the rows are text-heavy by design. Insetting
 * the divider to match the text leaves it looking like it belongs to the text
 * rather than to the list, so the padding lives on the contents and the rule
 * runs edge to edge — the same convention the sheet header and footer use.
 */
export const accountPanelRowClasses =
  'group flex items-start gap-3 border-b border-border/60 px-4 py-3 transition-colors last:border-b-0 hover:bg-surface-sunken';

export const accountPanelRowIconWrapClasses =
  'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-control bg-surface-sunken text-content-muted transition-colors group-hover:bg-surface-raised group-hover:text-content';

export const accountPanelRowIconClasses = 'h-4 w-4';

export const accountPanelRowBodyClasses = 'min-w-0 flex-1';

export const accountPanelRowLabelClasses = 'text-md font-semibold leading-snug text-content';

export const accountPanelRowDescriptionClasses = 'mt-0.5 text-xs leading-snug text-content-muted';

/** Trailing chevron: a destination, not a toggle. Fades in on hover so it never competes with the label. */
export const accountPanelRowChevronClasses =
  'mt-1.5 h-4 w-4 shrink-0 text-content-subtle opacity-0 transition-opacity group-hover:opacity-100';

/**
 * Footer.
 *
 * Signed out is destructive and sits apart from the destinations — separated by
 * a full-width divider and its own padding, so it cannot be reached by aiming
 * at the row above it.
 */
export const accountPanelFooterClasses = 'shrink-0 border-t border-border/70 p-3';

export const accountPanelSignOutClasses =
  'flex w-full items-center gap-3 rounded-control px-3 py-2.5 text-md font-semibold text-critical transition-colors hover:bg-critical-soft';

export const accountPanelSignOutIconClasses = 'h-4 w-4 shrink-0';

/** The nine-dot launcher at the head of the menu. */
export const accountPanelGridIconClasses = 'h-4 w-4';
